import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tabs, Form, Input, InputNumber, Select, DatePicker, Checkbox, Button, Card, Row, Col, Table, Modal, Popconfirm, Tag, notification, Typography, Spin, Descriptions, Space } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';

import { date as fmtDate } from '../../lib/format';
import { CountrySelect } from '../../components/CountrySelect';
const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH'];
const ORG_TYPES = [
  { value: 'CY_PRIVATE_COMPANY', label: 'CY Private Company (LTD / LLC)' },
  { value: 'CY_PUBLIC_COMPANY', label: 'CY Public Company (PLC)' },
  { value: 'CY_PARTNERSHIP', label: 'CY Partnership' },
  { value: 'OTHER', label: 'Other' },
];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => ({ value: i + 1, label: d }));
const DATE_FIELDS = ['incorporation_date', 'risk_classification_date', 'last_kyc_review_date', 'next_kyc_review_date', 'tin_registration_date', 'vat_registration_date', 'si_ame_registration_date'];
const toDate = (v) => (v ? moment(v) : null);
const fromDate = (v) => (v ? v.format('YYYY-MM-DD') : null);

/** Consistent two-column form grid: pass Form.Items as children; each takes half the width (full on small screens). */
function FormGrid({ children }) {
  return (
    <Row gutter={16}>
      {React.Children.map(children, (child) =>
        child ? (
          <Col xs={24} md={child.props['data-full'] ? 24 : 12}>
            {child}
          </Col>
        ) : null
      )}
    </Row>
  );
}

/** A register (officers / UBOs / KYC documents) of the firm itself: table + add modal + remove. */
function Register({ companyId, endpoint, title, rows, columns, fields, onChanged, canEdit, addLabel, dateFields = [] }) {
  const [open, setOpen] = React.useState(false);
  const [form] = Form.useForm();

  const save = () => {
    form.validateFields().then((values) => {
      const payload = { ...values };
      dateFields.forEach((f) => (payload[f] = fromDate(values[f])));
      request
        .post(`/companies/${companyId}/${endpoint}`, payload)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Saved' });
            setOpen(false);
            onChanged();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };
  const remove = (row) =>
    request
      .delete(`/companies/${companyId}/${endpoint}/${row.id}`)
      .then((r) => r.json())
      .then((r) => (r.status ? onChanged() : notification.error({ message: r.message })));

  return (
    <Card
      title={`${title} (${rows.length})`}
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        canEdit && (
          <Button size="small" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>
            {addLabel || 'Add'}
          </Button>
        )
      }
    >
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'Nothing recorded yet' }}
        columns={[
          ...columns,
          ...(canEdit
            ? [{ title: '', key: 'del', width: 50, render: (_, r) => (
                  <Popconfirm title="Remove this row?" onConfirm={() => remove(r)}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ) }]
            : []),
        ]}
      />
      <Modal title={addLabel || `Add ${title}`} open={open} onCancel={() => setOpen(false)} onOk={save} width={640}>
        <Form form={form} layout="vertical">
          <FormGrid>{fields}</FormGrid>
        </Form>
      </Modal>
    </Card>
  );
}

/** The firm's own company: Profile / ROC / Tax & SI / KYC / Settings / ScanNRead (xlsx Organization sheet). */
export default function CompanyDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const canEdit = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const [company, setCompany] = React.useState(null);
  const [companies, setCompanies] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [holidayYear, setHolidayYear] = React.useState(new Date().getFullYear());
  const [form] = Form.useForm();

  const fetchCompany = React.useCallback(() => {
    request
      .get(`/companies/${id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setCompany(r.data);
          const values = { ...r.data };
          DATE_FIELDS.forEach((f) => (values[f] = toDate(r.data[f])));
          form.setFieldsValue(values);
        } else {
          notification.error({ message: r.message });
        }
      });
  }, [id, form]);

  const importHolidays = () =>
    request
      .post(`/companies/${id}/holidays/import-cyprus`, { year: holidayYear })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: r.message });
          fetchCompany();
        } else {
          notification.error({ message: r.message });
        }
      });

  React.useEffect(() => {
    fetchCompany();
    request.get('/companies').then((r) => r.json()).then((r) => r.status && setCompanies(r.data));
  }, [fetchCompany]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = { ...values };
      DATE_FIELDS.forEach((f) => (payload[f] = fromDate(values[f])));
      delete payload.ClientOfficers;
      delete payload.ClientUBOs;
      delete payload.ClientKYCDocuments;
      setSaving(true);
      request
        .put(`/companies/${id}`, payload)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Company saved' });
            fetchCompany();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setSaving(false));
    });
  };

  if (!company) return <Spin style={{ marginTop: 40 }} />;

  const saveBar = canEdit && (
    <div style={{ marginTop: 8 }}>
      <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
        Save
      </Button>
    </div>
  );

  const officers = company.ClientOfficers || [];
  const officerColumns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Kind', dataIndex: 'officer_kind', render: (v) => v || 'INDIVIDUAL' },
    { title: 'ID / Reg. No.', dataIndex: 'id_number', render: (v) => v || '-' },
    { title: 'Nationality / Jurisdiction', dataIndex: 'nationality', render: (v) => v || '-' },
    { title: 'Appointed', dataIndex: 'appointment_date', render: (v) => v || '-' },
    { title: 'Resigned', dataIndex: 'resignation_date', render: (v) => v || '-' },
  ];
  const officerFields = (type) => [
    <Form.Item key="t" name="officer_type" initialValue={type} hidden><Input /></Form.Item>,
    <Form.Item key="k" name="officer_kind" label="Kind" initialValue="INDIVIDUAL"><Select options={[{ value: 'INDIVIDUAL', label: 'Individual' }, { value: 'CORPORATE', label: 'Corporate' }]} /></Form.Item>,
    <Form.Item key="n" name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>,
    <Form.Item key="i" name="id_number" label="ID / Passport / Reg. No."><Input /></Form.Item>,
    <Form.Item key="na" name="nationality" label="Nationality / Jurisdiction"><CountrySelect /></Form.Item>,
    ...(type === 'SHAREHOLDER' ? [<Form.Item key="s" name="shareholding_percent" label="Shareholding %"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>] : []),
    <Form.Item key="a" name="appointment_date" label={type === 'SHAREHOLDER' ? 'Since' : 'Appointed'}><DatePicker style={{ width: '100%' }} /></Form.Item>,
    <Form.Item key="r" name="resignation_date" label={type === 'SHAREHOLDER' ? 'Until' : 'Resigned'}><DatePicker style={{ width: '100%' }} /></Form.Item>,
    <Form.Item key="ad" name="address" label="Address" data-full><Input.TextArea rows={2} /></Form.Item>,
  ];

  const profileTab = (
    <Form form={form} layout="vertical">
      <Card title="Details" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="name" label="Registered name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="business_name" label="Business name / brand"><Input maxLength={150} /></Form.Item>
          <Form.Item name="short_name" label="Short name / code"><Input /></Form.Item>
          <Form.Item name="organization_type" label="Type of organization"><Select allowClear options={ORG_TYPES} /></Form.Item>
          <Form.Item name="principal_activities" label="Principal activities" data-full><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="parent_company_id" label="Parent (group) company">
            <Select allowClear options={companies.filter((c) => c.is_group && c.id !== company.id).map((c) => ({ value: c.id, label: c.name }))} />
          </Form.Item>
          <Form.Item name="is_group" valuePropName="checked" label=" "><Checkbox>This is a group (holding) company</Checkbox></Form.Item>
        </FormGrid>
      </Card>
      <Card title="Contact details" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="telephone" label="Telephone"><Input maxLength={40} /></Form.Item>
          <Form.Item name="fax" label="Fax"><Input maxLength={40} /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}><Input maxLength={120} /></Form.Item>
          <Form.Item name="website" label="Website"><Input maxLength={150} /></Form.Item>
          <Form.Item name="po_box" label="PO Box"><Input maxLength={40} /></Form.Item>
          <Form.Item name="language" label="Language"><Select allowClear options={[{ value: 'GREEK', label: 'Greek' }, { value: 'ENGLISH', label: 'English' }]} /></Form.Item>
          <Form.Item name="business_address" label="Business address"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="correspondence_address" label="Correspondence address"><Input.TextArea rows={2} /></Form.Item>
        </FormGrid>
      </Card>
      {saveBar}
    </Form>
  );

  const rocTab = (
    <div>
      <Form form={form} layout="vertical">
        <Card title="Registrar of Companies" size="small" style={{ marginBottom: 16 }}>
          <FormGrid>
            <Form.Item name="registration_number" label="Registration No."><Input /></Form.Item>
            <Form.Item name="jurisdiction" label="Jurisdiction"><CountrySelect /></Form.Item>
            <Form.Item name="incorporation_date" label="Incorporation date"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="country" label="Country"><CountrySelect /></Form.Item>
            <Form.Item name="address" label="Registered office address" data-full><Input.TextArea rows={2} /></Form.Item>
            <Form.Item name="city" label="City"><Input maxLength={80} /></Form.Item>
            <Form.Item name="district" label="District"><Input maxLength={80} /></Form.Item>
            <Form.Item name="postal_code" label="Postal code"><Input maxLength={20} /></Form.Item>
          </FormGrid>
          {saveBar}
        </Card>
      </Form>
      <Register companyId={id} endpoint="officers" title="Shareholders" addLabel="Add shareholder" canEdit={canEdit} onChanged={fetchCompany} dateFields={['appointment_date', 'resignation_date']}
        rows={officers.filter((o) => o.officer_type === 'SHAREHOLDER')}
        columns={[...officerColumns, { title: 'Holding %', dataIndex: 'shareholding_percent', render: (v) => (v !== null && v !== undefined ? `${Number(v)}%` : '-') }]}
        fields={officerFields('SHAREHOLDER')} />
      <Register companyId={id} endpoint="officers" title="Directors" addLabel="Add director" canEdit={canEdit} onChanged={fetchCompany} dateFields={['appointment_date', 'resignation_date']}
        rows={officers.filter((o) => o.officer_type === 'DIRECTOR')} columns={officerColumns} fields={officerFields('DIRECTOR')} />
      <Register companyId={id} endpoint="officers" title="Secretary" addLabel="Add secretary" canEdit={canEdit} onChanged={fetchCompany} dateFields={['appointment_date', 'resignation_date']}
        rows={officers.filter((o) => o.officer_type === 'SECRETARY')} columns={officerColumns} fields={officerFields('SECRETARY')} />
    </div>
  );

  const taxTab = (
    <Form form={form} layout="vertical">
      <Card title="Tax Department" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="tax_id" label="TIN"><Input /></Form.Item>
          <Form.Item name="tin_registration_date" label="TIN registration date"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="td_registered_name" label="TD registered name"><Input maxLength={150} /></Form.Item>
          <Form.Item name="district_office" label="District office"><Input maxLength={80} /></Form.Item>
        </FormGrid>
      </Card>
      <Card title="VAT" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="vat_number" label="VAT No."><Input maxLength={40} /></Form.Item>
          <Form.Item name="vat_registration_date" label="VAT registration date"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="vat_category" label="VAT category"><Input maxLength={60} /></Form.Item>
          <Form.Item name="first_vat_period" label="First VAT period"><Input maxLength={20} placeholder="e.g. 01-03/2026" /></Form.Item>
        </FormGrid>
      </Card>
      <Card title="Social Insurance" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="si_ame_number" label="SI - AME No. (employer)"><Input maxLength={40} /></Form.Item>
          <Form.Item name="si_ame_registration_date" label="AME registration date"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="si_aka_number" label="SI - AKA No."><Input maxLength={40} /></Form.Item>
          <Form.Item name="si_number" label="SI No. (legacy)"><Input /></Form.Item>
        </FormGrid>
      </Card>
      {saveBar}
    </Form>
  );

  const kycTab = (
    <div>
      <Form form={form} layout="vertical">
        <Card title="Risk classification" size="small" style={{ marginBottom: 16 }}>
          <FormGrid>
            <Form.Item name="risk_level" label="Risk level"><Select allowClear options={RISK_LEVELS.map((r) => ({ value: r, label: r }))} /></Form.Item>
            <Form.Item name="risk_classification_date" label="Classified on"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="last_kyc_review_date" label="Last KYC review"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="next_kyc_review_date" label="Next KYC review"><DatePicker style={{ width: '100%' }} /></Form.Item>
          </FormGrid>
          {saveBar}
        </Card>
      </Form>
      <Register companyId={id} endpoint="ubos" title="Ultimate Beneficial Owners" addLabel="Add UBO" canEdit={canEdit} onChanged={fetchCompany} dateFields={['date_of_birth']}
        rows={company.ClientUBOs || []}
        columns={[
          { title: 'Name', dataIndex: 'name' },
          { title: 'ID / Passport', dataIndex: 'id_number', render: (v) => v || '-' },
          { title: 'Nationality', dataIndex: 'nationality', render: (v) => v || '-' },
          { title: 'Date of birth', dataIndex: 'date_of_birth', render: (v) => v || '-' },
          { title: 'Ownership %', dataIndex: 'ownership_percent', render: (v) => (v !== null && v !== undefined ? `${Number(v)}%` : '-') },
          { title: 'Control', dataIndex: 'control_type', render: (v) => v || '-' },
        ]}
        fields={[
          <Form.Item key="n" name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>,
          <Form.Item key="i" name="id_number" label="ID / Passport"><Input /></Form.Item>,
          <Form.Item key="na" name="nationality" label="Nationality"><CountrySelect /></Form.Item>,
          <Form.Item key="d" name="date_of_birth" label="Date of birth"><DatePicker style={{ width: '100%' }} /></Form.Item>,
          <Form.Item key="o" name="ownership_percent" label="Ownership %"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>,
          <Form.Item key="c" name="control_type" label="Control"><Select allowClear options={['DIRECT', 'INDIRECT'].map((v) => ({ value: v, label: v }))} /></Form.Item>,
          <Form.Item key="a" name="address" label="Address" data-full><Input.TextArea rows={2} /></Form.Item>,
        ]} />
      <Register companyId={id} endpoint="kyc-documents" title="KYC documents" addLabel="Add document" canEdit={canEdit} onChanged={fetchCompany} dateFields={['issue_date', 'expiry_date']}
        rows={company.ClientKYCDocuments || []}
        columns={[
          { title: 'Type', dataIndex: 'document_type', render: (v) => v.replace(/_/g, ' ') },
          { title: 'Number', dataIndex: 'document_number', render: (v) => v || '-' },
          { title: 'Issued', dataIndex: 'issue_date', render: (v) => v || '-' },
          { title: 'Expires', dataIndex: 'expiry_date', render: (v) => (v ? <Tag color={v < moment().format('YYYY-MM-DD') ? 'red' : 'default'}>{v}</Tag> : '-') },
          { title: 'Country', dataIndex: 'issuing_country', render: (v) => v || '-' },
        ]}
        fields={[
          <Form.Item key="t" name="document_type" label="Document type" rules={[{ required: true }]}>
            <Select options={['PASSPORT_ID', 'PROOF_OF_ADDRESS', 'CORPORATE_REGISTRATION_CERTIFICATE', 'SHAREHOLDER_REGISTER', 'OTHER'].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }))} />
          </Form.Item>,
          <Form.Item key="n" name="document_number" label="Document number"><Input /></Form.Item>,
          <Form.Item key="i" name="issue_date" label="Issue date"><DatePicker style={{ width: '100%' }} /></Form.Item>,
          <Form.Item key="e" name="expiry_date" label="Expiry date"><DatePicker style={{ width: '100%' }} /></Form.Item>,
          <Form.Item key="c" name="issuing_country" label="Issuing country"><CountrySelect /></Form.Item>,
          <Form.Item key="no" name="notes" label="Notes" data-full><Input.TextArea rows={2} /></Form.Item>,
        ]} />
    </div>
  );

  const settingsTab = (
    <Form form={form} layout="vertical">
      <Card title="Calendar setup" size="small" style={{ marginBottom: 16 }}>
        <FormGrid>
          <Form.Item name="week_start_day" label="Week starts on" tooltip="First column of the weekly timesheet"><Select options={WEEKDAYS} /></Form.Item>
          <Form.Item name="standard_working_days_per_week" label="Working days per week" tooltip="Counted from the week start; the remaining days are shown as weekend on timesheets"><InputNumber min={1} max={7} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="standard_working_hours_per_day" label="Working hours per day"><InputNumber min={0} max={24} style={{ width: '100%' }} /></Form.Item>
        </FormGrid>
      </Card>
      {canEdit && (
        <Space style={{ marginBottom: 8 }}>
          <span>Import Cyprus public holidays for</span>
          <InputNumber min={1900} max={2099} value={holidayYear} onChange={setHolidayYear} style={{ width: 90 }} />
          <Button size="small" onClick={importHolidays}>Import</Button>
        </Space>
      )}
      <Register
        companyId={id}
        endpoint="holidays"
        title="Public & office holidays"
        rows={[...(company.Holidays || [])].sort((a, b) => (a.holiday_date < b.holiday_date ? -1 : 1))}
        onChanged={fetchCompany}
        canEdit={canEdit}
        addLabel="Add holiday"
        dateFields={['holiday_date']}
        columns={[
          { title: 'Date', dataIndex: 'holiday_date', width: 120, render: (v) => fmtDate(v) },
          { title: 'Day', key: 'd', width: 100, render: (_, r) => moment(r.holiday_date).format('dddd') },
          { title: 'Holiday', dataIndex: 'name' },
          { title: 'Type', dataIndex: 'type', width: 100, render: (v) => <Tag color={v === 'PUBLIC' ? 'red' : 'orange'}>{v}</Tag> },
        ]}
        fields={[
          <Form.Item key="d" name="holiday_date" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>,
          <Form.Item key="t" name="type" label="Type" initialValue="OFFICE"><Select options={[{ value: 'PUBLIC', label: 'Public holiday' }, { value: 'OFFICE', label: 'Office holiday' }]} /></Form.Item>,
          <Form.Item key="n" name="name" label="Name" rules={[{ required: true }]} data-full><Input /></Form.Item>,
        ]}
      />
      <Card title="The firm's own books" size="small" style={{ marginBottom: 16 }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Financial year, presentation currency, the default accounts for automatic postings of invoices / receipts / credit notes and the ScanNRead connection are settings of the books themselves. The firm's own books are listed with every client's under <Link to="/books">Accounting → Client Books</Link>.
        </Typography.Paragraph>
      </Card>
      <Card title="Proposal template" size="small" style={{ marginBottom: 16 }}>
        <Form.Item name="proposal_template" label="Default 'Proposal details' text" tooltip="Pre-filled on every new proposal for this company">
          <Input.TextArea rows={6} placeholder="Standard scope, deliverables, fee terms and conditions text for proposals" />
        </Form.Item>
      </Card>
      {saveBar}
    </Form>
  );

  const scanTab = (
    <Card title="ScanNRead integration" size="small" style={{ maxWidth: 640 }}>
      <Typography.Paragraph type="secondary">
        ScanNRead is the scanned-document intake of one set of books: each client's books (and the firm's own) connect to their own ScanNRead tenant. Set the customer id and API token per books under <Link to="/books">Accounting → Client Books → Settings</Link>.
      </Typography.Paragraph>
    </Card>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {company.name} {company.is_group && <Tag color="blue">group</Tag>}
          </Typography.Title>
          <Typography.Text type="secondary">
            {isSystemAdmin && <Link to="/companies">Companies</Link>}
            {isSystemAdmin && ' / '}
            {[ORG_TYPES.find((t) => t.value === company.organization_type)?.label, company.registration_number && `Reg. ${company.registration_number}`, company.vat_number && `VAT ${company.vat_number}`].filter(Boolean).join(' - ') || 'Company profile'}
          </Typography.Text>
        </div>
        <Descriptions size="small" column={1} style={{ flex: '0 0 260px' }}>
          <Descriptions.Item label="Risk">{company.risk_level ? <Tag color={{ LOW: 'green', MEDIUM: 'gold', HIGH: 'red' }[company.risk_level]}>{company.risk_level}</Tag> : '-'}</Descriptions.Item>
          <Descriptions.Item label="Next KYC review">{company.next_kyc_review_date || '-'}</Descriptions.Item>
        </Descriptions>
      </div>

      <Tabs
        items={[
          { key: 'profile', label: 'Profile', children: profileTab },
          { key: 'roc', label: 'ROC', children: rocTab },
          { key: 'tax', label: 'Tax & SI', children: taxTab },
          { key: 'kyc', label: 'KYC', children: kycTab },
          { key: 'settings', label: 'Settings', children: settingsTab },
          ...(isSystemAdmin ? [{ key: 'scan-n-read', label: 'ScanNRead', children: scanTab }] : []),
        ]}
      />
    </div>
  );
}
